/**
 * Offres & interrupteurs côté client.
 * La source de vérité est le serveur (`/auth/me` → user.features / user.required_plans) ;
 * ce module ne fait que lire ces valeurs et fournir les libellés.
 */

export const PLAN_ORDER = ["essentiel", "conversion", "performance", "sur_mesure"];

export const PLAN_LABELS = {
  essentiel: "Essentiel",
  conversion: "Conversion",
  performance: "Performance",
  sur_mesure: "Sur-mesure",
};

export const PLAN_STATUS_LABELS = {
  trial: "Essai",
  active: "Actif",
  past_due: "Impayé",
  canceled: "Résilié",
};

export const ROLE_LABELS = {
  owner: "Propriétaire",
  admin: "Admin",
  member: "Membre",
  viewer: "Lecteur",
};

export const PRICING_URL = (process.env.REACT_APP_PRICING_URL || "https://groupe-aulia.io/#tarifs").trim();

/** Libellé court d'un interrupteur (repli si le catalogue serveur n'est pas chargé). */
export const FEATURE_LABELS = {
  page_widget: "Page de contact + widget",
  brand_colors_logo: "Couleurs et logo",
  custom_texts: "Textes personnalisés",
  custom_font_background: "Police et arrière-plan au choix",
  live_preview: "Aperçu en direct",
  calendar_booking: "Rendez-vous",
  click_to_call: "Appel en un clic",
  limit_questions: "Questions par parcours",
  scoring: "Score automatique",
  qualification_methods: "Méthodes BANT, ANUM, FAINT, MEDDIC",
  limit_flows: "Nombre de parcours",
  lead_profile: "Fiche lead",
  hot_lead_alert: "Alerte lead chaud",
  limit_leads_month: "Leads par mois",
  lead_routing: "Routage des leads",
  scoring_emails: "Mails de scoring",
  auto_followups: "Relances automatiques",
  nurturing: "Nurturing des leads tièdes",
  kpi_basic: "Statistiques de base",
  kpi_sources: "Meilleurs canaux d'acquisition",
  kpi_funnel: "Parcours des visiteurs",
  kpi_time_saved: "Temps commercial gagné",
  kpi_history: "Historique mois par mois",
  monthly_report: "Rapport mensuel commenté",
  crm_integration: "Connexion CRM",
  limit_users: "Nombre d'utilisateurs",
};

export const planLabel = (plan) => PLAN_LABELS[plan] || plan || "";
export const planStatusLabel = (status) => PLAN_STATUS_LABELS[status] || status || "";
export const roleLabel = (role) => ROLE_LABELS[role] || role || "";
export const featureLabel = (code) => FEATURE_LABELS[code] || code;

/** Vrai si l'interrupteur est allumé pour le compte (booléen ou limite > 0 / illimitée). */
export function hasFeature(user, code) {
  const features = user?.features;
  if (!features || !(code in features)) return true; // pas encore chargé → ne jamais bloquer l'UI
  const v = features[code];
  if (typeof v === "boolean") return v;
  if (v === null || v === undefined) return true; // limite illimitée
  return Number(v) > 0;
}

/** Limite numérique (null = illimité). */
export function featureLimit(user, code) {
  const v = user?.features?.[code];
  if (v === null || v === undefined || typeof v === "boolean") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Offre minimale qui débloque l'interrupteur (pour le texte du cadenas). */
export function requiredPlan(user, code) {
  return user?.required_plans?.[code] || null;
}

export function lockMessage(user, code) {
  const plan = requiredPlan(user, code);
  if (plan) return `Disponible avec l'offre ${planLabel(plan)}`;
  return "Non inclus dans votre offre";
}

/** Extrait un message lisible d'une erreur API 403 feature_locked / limit_reached / role_forbidden. */
export function apiErrorMessage(err, fallback = "Action impossible") {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (typeof detail === "object" && detail.message) return detail.message;
  return fallback;
}

export function isFeatureLockedError(err) {
  const detail = err?.response?.data?.detail;
  return !!(detail && typeof detail === "object" && (detail.code === "feature_locked" || detail.code === "limit_reached"));
}

export const canWrite = (user) => (user?.role || "owner") !== "viewer" && !user?.account?.read_only;
/** Propriétaire ou Admin du compte (invitations, offre, facturation). */
export const isOwner = (user) => ["owner", "admin"].includes(user?.role || "owner");
